import { InputCheckbox } from '@creditornot/cb-checkbox';
import { DataTable } from '@creditornot/cb-data-table';
import { cssThemedValue, Spinner } from '@creditornot/cb-ingredients';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { addVenueToMenuApiUser, removeVenueToMenuApiUser } from 'apis/restaurant-api/merchant-admin/menu-api-users/api';
import { NullableMenuApiCredentialMetadata } from 'apis/restaurant-api/merchant-admin/menu-api-users/types';
import { MenuApiCredentialMetadata } from 'apis/restaurant-api/merchant-admin/menu-api-users/types';
import {
  putVenueInOrderApiCredentials,
  removeVenueInOrderApiCredentials,
} from 'apis/restaurant-api/merchant-admin/order-api-credentials/api';
import { NullableOrderApiCredentials } from 'apis/restaurant-api/merchant-admin/order-api-credentials/types';
import { OrderApiCredentials } from 'apis/restaurant-api/merchant-admin/order-api-credentials/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { localize } from 'common/utils';

import { IntegrationAccessTypes } from './types';
import { useMerchantContext } from '../../merchant/MerchantContext';

const VenueAddress = styled.div`
  color: ${cssThemedValue('textSecondary')};
`;

const LoadingSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-block-start: 40px;
  padding-block-end: 40px;
`;

type VenueApiHeader = {
  record: VenueApiAccess;
};

type VenueApiAccess = {
  id: string;
  name: string;
  address: string;
  menuApiAccess: boolean;
  orderApiAccess: boolean;
  sftpIntegrationAccess: boolean;
};

type ApiVenuesProps = {
  menuApiMetadata: NullableMenuApiCredentialMetadata;
  orderApiMetadata: NullableOrderApiCredentials;
  merchantId: string;
};

const VenuesWithApiAccess = ({ menuApiMetadata, orderApiMetadata, merchantId }: ApiVenuesProps) => {
  const intl = useIntl();
  const { merchantVenues } = useMerchantContext();
  const [venuesWithAccess, setVenuesWithAccess] = useState<VenueApiAccess[]>([]);

  // When we have a lot of venues, it takes time to map them all!
  const isLoading = venuesWithAccess.length !== merchantVenues.length;

  useEffect(() => {
    if ((merchantVenues?.length ?? 0) === 0) {
      return;
    }

    const toVenueWithApiAccess = (venue: Venue) => ({
      id: venue.id,
      name: localize(venue.name, intl.locale),
      address: venue.address,
      menuApiAccess: menuApiMetadata ? !!menuApiMetadata.venues?.find(v => v === venue.id) : false,
      orderApiAccess: orderApiMetadata ? !!orderApiMetadata.venues?.find(v => v === venue.id) : false,
      sftpIntegrationAccess: false,
    });

    const mappedVenues = merchantVenues.map(toVenueWithApiAccess);

    setVenuesWithAccess(mappedVenues);
  }, [intl.locale, menuApiMetadata, merchantVenues, orderApiMetadata, setVenuesWithAccess]);

  const onMenuApiVenueAccessChange = useCallback(
    (selected: boolean, menuApiMetadata: MenuApiCredentialMetadata, venueId: string) => {
      if (selected) {
        addVenueToMenuApiUser(merchantId, menuApiMetadata.username, venueId);
      } else {
        removeVenueToMenuApiUser(merchantId, menuApiMetadata.username, venueId);
      }
    },
    [merchantId],
  );

  const onOrderApiVenueAccessChange = useCallback(
    (selected: boolean, orderApiMetadata: OrderApiCredentials, venueId: string) => {
      if (selected) {
        putVenueInOrderApiCredentials(merchantId, orderApiMetadata.id, venueId);
      } else {
        removeVenueInOrderApiCredentials(merchantId, orderApiMetadata.id, venueId);
      }
    },
    [merchantId],
  );

  const handleAccessChange = useCallback(
    (venue: VenueApiAccess, apiAccessType: IntegrationAccessTypes) => {
      const selectedValue = !venue[apiAccessType];
      const updatedVenues = venuesWithAccess.map((v: VenueApiAccess) => {
        if (v.id !== venue.id) {
          return v;
        }
        return {
          ...v,
          [apiAccessType]: selectedValue,
        };
      });

      if (apiAccessType === IntegrationAccessTypes.MENU_API && menuApiMetadata?.username) {
        onMenuApiVenueAccessChange(selectedValue, menuApiMetadata, venue.id);
      } else if (apiAccessType === IntegrationAccessTypes.ORDER_API && orderApiMetadata) {
        onOrderApiVenueAccessChange(selectedValue, orderApiMetadata, venue.id);
      }

      setVenuesWithAccess(updatedVenues);
    },
    [menuApiMetadata, onMenuApiVenueAccessChange, onOrderApiVenueAccessChange, orderApiMetadata, venuesWithAccess],
  );

  const columns = [
    {
      id: 'name',
      name: intl.formatMessage({ id: 'merchant-integrations.venue-name' }),
      render: ({ record: venue }: VenueApiHeader) => (
        <>
          <div>{venue.name}</div>
          <VenueAddress>{venue.address}</VenueAddress>
        </>
      ),
    },
    {
      id: 'id',
      name: intl.formatMessage({ id: 'merchant-integrations.venue-id' }),
      render: ({ record: venue }: VenueApiHeader) => venue.id,
    },
  ];

  if (orderApiMetadata) {
    columns.push({
      id: 'order-api-access',
      name: intl.formatMessage({ id: 'merchant-integrations.order-api.access' }),
      render: ({ record: venue }: VenueApiHeader) => (
        <InputCheckbox
          checked={venue.orderApiAccess}
          onChange={() => handleAccessChange(venue, IntegrationAccessTypes.ORDER_API)}
        />
      ),
    });
  }

  if (menuApiMetadata?.username) {
    columns.push({
      id: 'menu-api-access',
      name: intl.formatMessage({ id: 'merchant-integrations.menu-api.access' }),
      render: ({ record: venue }: VenueApiHeader) => (
        <InputCheckbox
          checked={venue.menuApiAccess}
          onChange={() => handleAccessChange(venue, IntegrationAccessTypes.MENU_API)}
        />
      ),
    });
  }

  if (isLoading) {
    return (
      <LoadingSpinnerContainer>
        <Spinner size="large" />
      </LoadingSpinnerContainer>
    );
  }

  return (
    <DataTable
      dataSource={venuesWithAccess}
      getRowKey={record => record.id}
      horizontalPadding="wide"
      columns={columns}
    />
  );
};

export default VenuesWithApiAccess;
